import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { enumsectionToI18nKey } from "../../../../generated/enums";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { Button } from "../../../common/Button";
import { Header, HeaderAction } from "../../../Header";
import { PageTour } from "../../../tour/PageTour";
import { breedingsTourSteps } from "../../../tour/steps/pageSteps";
import { setupBreedingPath } from "../../setup/setupRoutes";
import { sectionToIcon } from "../../../type/default";
import { AssociateDeviceModal } from "../../device/AssociateDeviceModal";
import { breederBreedingPath } from "./breedingRoutes";
import { FarmerBreedingCard } from "./FarmerBreedingCard";
import {
  breedingNamesOf,
  UnassignedDevicesCard,
} from "./UnassignedDevicesCard";
import { useFarmerBreedings } from "./useFarmerBreedings";

export const BreedingPage = () => {
  const navigate = useNavigate();
  const { breedings, unassignedDevices, isLoading, hasError, reload } =
    useFarmerBreedings();
  const [isAssociateDeviceOpen, setIsAssociateDeviceOpen] = useState(false);
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  // Compte de consultation : un collaborateur éleveur ne crée ni ne modifie rien.
  const canManageBreedings = getAccountStorage()?.type === "breeder";

  // Un seul élevage : la liste n'apporte rien, on ouvre directement sa fiche.
  if (breedings.length === 1) {
    return (
      <Navigate to={breederBreedingPath(breedings[0].breeding.id)} replace />
    );
  }

  return (
    <div className="p-4">
      <Header
        id="tour-breedings-header"
        title={t(enumsectionToI18nKey["section_breedings"])}
        icon={sectionToIcon.section_breedings}
        // Le tutoriel démarre seul : sans la liste, il se jouerait tronqué et serait
        // marqué comme vu.
        help={
          isLoading || hasError ? undefined : (
            <PageTour id="breedings" steps={breedingsTourSteps} />
          )
        }
        actions={
          canManageBreedings ? (
            <>
              <HeaderAction
                icon="plus"
                label={t("breeding-create")}
                onClick={() => navigate(setupBreedingPath())}
              />
              <HeaderAction
                icon="link"
                variant="secondary"
                label={t("device-link-title")}
                onClick={() => setIsAssociateDeviceOpen(true)}
              />
            </>
          ) : undefined
        }
      />

      {isLoading ? (
        <p className="mt-6 text-tertiary">{t("loading")}</p>
      ) : hasError ? (
        <p className="mt-6 text-red-500">{t("breedings-load-error")}</p>
      ) : (
        <div
          id="tour-breedings-list"
          className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {breedings.length > 0 ? (
            breedings.map((entry) => (
              <FarmerBreedingCard
                key={entry.breeding.id}
                data={entry}
                onSelect={() =>
                  navigate(breederBreedingPath(entry.breeding.id))
                }
              />
            ))
          ) : (
            <div className="sm:col-span-2 xl:col-span-3 text-center py-10 px-4 rounded-xl bg-grey-light/50">
              <p className="text-grey-medium text-sm mb-4">
                {t("breedings-empty")}
              </p>
              {canManageBreedings && (
                <Button onClick={() => navigate(setupBreedingPath())}>
                  {t("setup-start")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {!isLoading && !hasError && (
        <div className="mt-6">
          <UnassignedDevicesCard
            groups={unassignedDevices}
            breedingNames={breedingNamesOf(breedings)}
            onAttached={reload}
            showCompany={false}
          />
        </div>
      )}

      {canManageBreedings && (
        <AssociateDeviceModal
          isOpen={isAssociateDeviceOpen}
          onClose={() => setIsAssociateDeviceOpen(false)}
          onAssociated={reload}
        />
      )}
    </div>
  );
};
